/**
 * ══════════════════════════════════════════════════════════════════════════
 * SERVICIO API - ATRIBUTOS DINÁMICOS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Cliente HTTP para API REST de atributos dinámicos
 *
 * @author Sistema SISPRO ERP
 * @version 1.0.0
 */

import api from './api.js';

const BASE_PATH = '/atributos-dinamicos';

// ══════════════════════════════════════════════════════════════════════════
// ENTIDADES
// ══════════════════════════════════════════════════════════════════════════

/**
 * Obtener todas las entidades que soportan atributos dinámicos
 * @returns {Promise<Array>} Lista de entidades
 */
export async function getEntidades() {
  const response = await api.get(`${BASE_PATH}/entidades`);
  return response.data;
}

// ══════════════════════════════════════════════════════════════════════════
// DEFINICIONES DE ATRIBUTOS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Obtener todas las definiciones de atributos
 * @returns {Promise<Array>} Lista de definiciones
 */
export async function getDefiniciones() {
  const response = await api.get(`${BASE_PATH}/definiciones`);
  return response.data;
}

/**
 * Obtener todas las definiciones de atributos modelados para filtros criteria
 * @returns {Promise<Array>} Lista de definiciones modeladas para filtros criteria
 */
export async function getDefinicionesFiltrosCriteria() {
  const response = await api.get(`${BASE_PATH}/definiciones/filtros-criteria`);
  return response.data;
}

/**
 * Obtener definiciones visibles con flag Editable calculado por backend
 * @param {string} nombreEntidad - Nombre de la entidad (Material, Proceso, Recurso)
 * @param {Object} opciones - Opciones de filtrado
 * @param {number} opciones.modulo - Bitfield del módulo
 * @param {number} opciones.codigoElementoTipo - Código del tipo de elemento
 * @param {number} opciones.codigoMaterialCategoria - Código de la categoría de material
 * @returns {Promise<Array>} Definiciones visibles con campo Editable
 */
export async function getDefinicionesVisiblesConEditable(nombreEntidad, opciones = {}) {
  const params = new URLSearchParams();

  if (opciones.modulo) params.append('modulo', opciones.modulo.toString());
  if (opciones.codigoElementoTipo)
    params.append('codigoElementoTipo', opciones.codigoElementoTipo.toString());
  if (opciones.codigoMaterialCategoria)
    params.append('codigoMaterialCategoria', opciones.codigoMaterialCategoria.toString());
  if (opciones.codigoProceso) params.append('codigoProceso', opciones.codigoProceso.toString());

  const queryString = params.toString();
  const url = `${BASE_PATH}/definiciones/entidad/${nombreEntidad}/visibles-editables${queryString ? `?${queryString}` : ''}`;

  const response = await api.get(url);
  return response.data;
}

/**
 * Crear nueva definición de atributo
 * @param {Object} data - Datos de la definición
 * @returns {Promise<Object>} Definición creada
 */
export async function createDefinicion(data) {
  const response = await api.post(`${BASE_PATH}/definiciones`, data);
  return response.data;
}

/**
 * Actualizar definición (con versionado automático)
 * @param {number} codigo - Código de la definición
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Definición actualizada
 */
export async function updateDefinicion(codigo, data) {
  const response = await api.put(`${BASE_PATH}/definiciones/${codigo}`, data);
  return response.data;
}

/**
 * Eliminar definición (con registro en historial)
 * @param {number} codigo - Código de la definición
 * @returns {Promise<Object>} Resultado
 */
export async function deleteDefinicion(codigo) {
  const response = await api.delete(`${BASE_PATH}/definiciones/${codigo}`);
  return response.data;
}

// ══════════════════════════════════════════════════════════════════════════
// OPCIONES (para SELECT/MULTISELECT)
// ══════════════════════════════════════════════════════════════════════════

/**
 * Obtener opciones de un atributo
 * @param {number} codigoAtributo - Código del atributo
 * @returns {Promise<Array>} Lista de opciones
 */
export async function getOpciones(codigoAtributo) {
  const response = await api.get(`${BASE_PATH}/opciones/atributo/${codigoAtributo}`);
  return response.data;
}

/**
 * Crear nueva opción
 * @param {Object} data - Datos de la opción
 * @returns {Promise<Object>} Opción creada
 */
export async function createOpcion(data) {
  const response = await api.post(`${BASE_PATH}/opciones`, data);
  return response.data;
}

/**
 * Actualizar opción
 * @param {number} codigo - Código de la opción
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Opción actualizada
 */
export async function updateOpcion(codigo, data) {
  const response = await api.put(`${BASE_PATH}/opciones/${codigo}`, data);
  return response.data;
}

/**
 * Eliminar opción
 * @param {number} codigo - Código de la opción
 * @returns {Promise<Object>} Resultado
 */
export async function deleteOpcion(codigo) {
  const response = await api.delete(`${BASE_PATH}/opciones/${codigo}`);
  return response.data;
}

// ══════════════════════════════════════════════════════════════════════════
// RELACIONES ENTIDAD-ATRIBUTO
// ══════════════════════════════════════════════════════════════════════════

/**
 * Obtener relaciones de una entidad
 * @param {number} codigoEntidad - Código de la entidad
 * @returns {Promise<Array>} Lista de relaciones
 */
export async function getRelaciones(codigoEntidad) {
  const response = await api.get(`${BASE_PATH}/relaciones/entidad/${codigoEntidad}`);
  return response.data;
}

/**
 * Crear nueva relación entidad-atributo
 * @param {Object} data - Datos de la relación
 * @returns {Promise<Object>} Relación creada
 */
export async function createRelacion(data) {
  const response = await api.post(`${BASE_PATH}/relaciones`, data);
  return response.data;
}

/**
 * Actualizar relación
 * @param {number} codigo - Código de la relación
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Relación actualizada
 */
export async function updateRelacion(codigo, data) {
  const response = await api.put(`${BASE_PATH}/relaciones/${codigo}`, data);
  return response.data;
}

/**
 * Eliminar relación
 * @param {number} codigo - Código de la relación
 * @returns {Promise<Object>} Resultado
 */
export async function deleteRelacion(codigo) {
  const response = await api.delete(`${BASE_PATH}/relaciones/${codigo}`);
  return response.data;
}

// ══════════════════════════════════════════════════════════════════════════
// CRITERIOS DE VISUALIZACIÓN
// ══════════════════════════════════════════════════════════════════════════

/**
 * Actualizar criterios de visualización por módulo (bitfield)
 * @param {number} codigoRelacion - Código de la relación
 * @param {number} modulo - Valor bitfield del módulo
 * @returns {Promise<Object>} Relación actualizada
 */
export async function updateCriteriosModulo(codigoRelacion, modulo) {
  const response = await api.put(`${BASE_PATH}/relaciones/${codigoRelacion}/modulo`, { modulo });
  return response.data;
}

/**
 * Obtener criterios de visualización por ElementoTipo
 * @param {number} codigoRelacion - Código de la relación
 * @returns {Promise<Array>} Lista de criterios
 */
export async function getCriteriosElementoTipo(codigoRelacion) {
  const response = await api.get(`${BASE_PATH}/relaciones/${codigoRelacion}/elemento-tipos`);
  return response.data;
}

/**
 * Actualizar criterios de ElementoTipo en lote
 * @param {number} codigoRelacion - Código de la relación
 * @param {Array<{codigoElementoTipo: number, visible: boolean}>} criterios - Lista de criterios
 * @returns {Promise<Array>} Criterios actualizados
 */
export async function updateCriteriosElementoTipo(codigoRelacion, criterios) {
  const response = await api.put(`${BASE_PATH}/relaciones/${codigoRelacion}/elemento-tipos`, {
    criterios,
  });
  return response.data;
}

/**
 * Obtener criterios de visualización por MaterialCategoria
 * @param {number} codigoRelacion - Código de la relación
 * @returns {Promise<Array>} Lista de criterios
 */
export async function getCriteriosMaterialCategoria(codigoRelacion) {
  const response = await api.get(`${BASE_PATH}/relaciones/${codigoRelacion}/material-categorias`);
  return response.data;
}

/**
 * Actualizar criterios de MaterialCategoria en lote
 * @param {number} codigoRelacion - Código de la relación
 * @param {Array<{codigoMaterialCategoria: number, visible: boolean}>} criterios - Lista de criterios
 * @returns {Promise<Array>} Criterios actualizados
 */
export async function updateCriteriosMaterialCategoria(codigoRelacion, criterios) {
  const response = await api.put(`${BASE_PATH}/relaciones/${codigoRelacion}/material-categorias`, {
    criterios,
  });
  return response.data;
}

/**
 * Obtener todos los ElementosTipo disponibles
 * @returns {Promise<Array>} Lista de ElementosTipo
 */
export async function getAllElementosTipo() {
  const response = await api.get(`${BASE_PATH}/elemento-tipos`);
  return response.data;
}

/**
 * Obtener todas las MaterialCategorias disponibles
 * @returns {Promise<Array>} Lista de MaterialCategorias
 */
export async function getAllMaterialCategorias() {
  const response = await api.get(`${BASE_PATH}/material-categorias`);
  return response.data;
}

/**
 * Obtener todos los Procesos disponibles
 * @returns {Promise<Array>} Lista de Procesos
 */
export async function getAllProcesos() {
  const response = await api.get(`${BASE_PATH}/procesos`);
  return response.data;
}

// ══════════════════════════════════════════════════════════════════════════
// GRILLA UNIFICADA (NUEVO)
// ══════════════════════════════════════════════════════════════════════════

/**
 * Obtener criterios unificados (visible + editable por módulo) para ElementoTipo
 * @param {number} codigoRelacion - Código de la relación
 * @returns {Promise<Array>} Lista de criterios unificados por ElementoTipo
 */
export async function getCriteriosElementoTipoUnificado(codigoRelacion) {
  const response = await api.get(
    `${BASE_PATH}/relaciones/${codigoRelacion}/elemento-tipos-unificado`,
  );
  return response.data;
}

/**
 * Actualizar criterios unificados de ElementoTipo en lote
 * @param {number} codigoRelacion - Código de la relación
 * @param {Array<{codigoElementoTipo: number, moduloVisible?: number, moduloEditable?: number}>} criterios - Lista de criterios
 * @returns {Promise<Array>} Criterios actualizados
 */
export async function updateCriteriosElementoTipoUnificado(codigoRelacion, criterios) {
  const response = await api.put(
    `${BASE_PATH}/relaciones/${codigoRelacion}/elemento-tipos-unificado`,
    { criterios },
  );
  return response.data;
}

/**
 * Obtener criterios unificados (visible + editable por módulo) para MaterialCategoria
 * @param {number} codigoRelacion - Código de la relación
 * @returns {Promise<Array>} Lista de criterios unificados por MaterialCategoria
 */
export async function getCriteriosMaterialCategoriaUnificado(codigoRelacion) {
  const response = await api.get(
    `${BASE_PATH}/relaciones/${codigoRelacion}/material-categorias-unificado`,
  );
  return response.data;
}

/**
 * Actualizar criterios unificados de MaterialCategoria en lote
 * @param {number} codigoRelacion - Código de la relación
 * @param {Array<{codigoMaterialCategoria: number, moduloVisible?: number, moduloEditable?: number}>} criterios - Lista de criterios
 * @returns {Promise<Array>} Criterios actualizados
 */
export async function updateCriteriosMaterialCategoriaUnificado(codigoRelacion, criterios) {
  const response = await api.put(
    `${BASE_PATH}/relaciones/${codigoRelacion}/material-categorias-unificado`,
    { criterios },
  );
  return response.data;
}

/**
 * Obtener criterios unificados (visible + editable por módulo) para Procesos
 * @param {number} codigoRelacion - Código de la relación
 * @returns {Promise<Array>} Lista de criterios unificados por Proceso
 */
export async function getCriteriosProcesosUnificado(codigoRelacion) {
  const response = await api.get(`${BASE_PATH}/relaciones/${codigoRelacion}/procesos-unificado`);
  return response.data;
}

/**
 * Actualizar criterios unificados de Procesos en lote
 * @param {number} codigoRelacion - Código de la relación
 * @param {Array<{codigoProceso: number, moduloVisible?: number, moduloEditable?: number}>} criterios - Lista de criterios
 * @returns {Promise<Array>} Criterios actualizados
 */
export async function updateCriteriosProcesosUnificado(codigoRelacion, criterios) {
  const response = await api.put(`${BASE_PATH}/relaciones/${codigoRelacion}/procesos-unificado`, {
    criterios,
  });
  return response.data;
}

export default {
  // Entidades
  getEntidades,

  // Definiciones
  getDefiniciones,
  getDefinicionesFiltrosCriteria,
  getDefinicionesVisiblesConEditable,
  createDefinicion,
  updateDefinicion,
  deleteDefinicion,

  // Opciones
  getOpciones,
  createOpcion,
  updateOpcion,
  deleteOpcion,

  // Relaciones
  getRelaciones,
  createRelacion,
  updateRelacion,
  deleteRelacion,

  // Criterios de visualización
  updateCriteriosModulo,
  getCriteriosElementoTipo,
  updateCriteriosElementoTipo,
  getCriteriosMaterialCategoria,
  updateCriteriosMaterialCategoria,
  getAllElementosTipo,
  getAllMaterialCategorias,
  getAllProcesos,

  // Grilla unificada (nuevo)
  getCriteriosElementoTipoUnificado,
  updateCriteriosElementoTipoUnificado,
  getCriteriosMaterialCategoriaUnificado,
  updateCriteriosMaterialCategoriaUnificado,
  getCriteriosProcesosUnificado,
  updateCriteriosProcesosUnificado,
};
